import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Loader2, Plus, Trash2, Sparkles, Lock, Zap, ArrowUpRight, Power, Film, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import SafeThumb from "@/components/SafeThumb";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";

// NOTE: no `message_template` field in this form on purpose — Story-to-DM
// sends only the product card (image + price + "Select this" button), no
// separate text message the way Comment-to-DM's private reply does. The
// column still exists on story_automation_rules for future use, but nothing
// currently reads it, so it's left out of this UI. Confirm before wiring it
// up if that changes.
const EMPTY_FORM = {
  product_id: "",
  story_media_id: "",
  thumbnail_url: "",
  match_type: "keyword", // 'keyword' | 'any'
  keywords: "",
};

// Deliberately NOT a live countdown — the actual deletion window (see backend:
// story_automation_rules.expires_at, a flat 50h buffer) is an internal safety
// margin, not something to surface to merchants. Showing real remaining hours
// would contradict the "24h / 48h for IG Plus" framing they see below, so this
// only ever reports expired/not-expired, never a number.
function isRuleExpired(expiresAt) {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

export default function StoryAutomationsPanel({ merchant }) {
  const enabled = merchant?.plan === "growth" || merchant?.plan === "pro";
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const formRef = useRef(null);
  // scrollIntoView, not window.scrollTo(0,0) — the form sits below the gradient
  // header banner, so scrolling to page-top lands above the dashboard nav, not
  // at the form itself.
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Tracks which rule row currently has a Pause/Activate request in flight,
  // so only that row's button shows a spinner + disables — not the whole list.
  const [togglingId, setTogglingId] = useState(null);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [igStories, setIgStories] = useState([]);
  const [igStoriesLoading, setIgStoriesLoading] = useState(false);
  const [igStoriesFetched, setIgStoriesFetched] = useState(false);
  const [igStoriesError, setIgStoriesError] = useState("");

  // `silent` skips the full-panel "Loading…" state — used when refreshing
  // after a row-level action (toggle) where we don't want the whole rules
  // list to disappear behind a loading message, just the one button.
  const load = useCallback(
    (opts = {}) => {
      if (!enabled) {
        setLoading(false);
        return Promise.resolve();
      }
      if (!opts.silent) setLoading(true);
      return api
        .get("/merchant/story-automations")
        .then((r) => setRules(r.data.items || []))
        .catch(() => setRules([]))
        .finally(() => setLoading(false));
    },
    [enabled]
  );

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [load]);

  useEffect(() => {
    if (!enabled) {
      setCatalogLoading(false);
      return;
    }
    setCatalogLoading(true);
    api
      .get("/catalog")
      .then((r) => setCatalog((r.data.items || []).filter((p) => p.active)))
      .catch(() => setCatalog([]))
      .finally(() => setCatalogLoading(false));
  }, [enabled]);

  // Stories are short-lived, so unlike the post-picker for comment automation,
  // this list is always fetched fresh — no caching beyond the current form
  // session. Gated on igStoriesFetched (not igStories.length) so a failed
  // fetch tries once, then waits for an explicit Retry.
  const fetchIgStories = useCallback(() => {
    setIgStoriesLoading(true);
    setIgStoriesError("");
    api
      .get("/merchant/instagram-stories")
      .then((r) => setIgStories(r.data.items || []))
      .catch((e) => {
        setIgStoriesError(
          e?.response?.data?.error === "instagram_not_connected"
            ? "Reconnect Instagram to pick from your live stories."
            : "Couldn't load your active stories — try again in a moment."
        );
      })
      .finally(() => {
        setIgStoriesLoading(false);
        setIgStoriesFetched(true);
      });
  }, []);

  useEffect(() => {
    if (!enabled || igStoriesFetched || igStoriesLoading) return;
    fetchIgStories();
  }, [enabled, igStoriesFetched, igStoriesLoading, fetchIgStories]);

  // Group flat rule list by story so the dashboard reads "one card per story,
  // one row per keyword/product" — mirrors how the merchant actually thinks
  // about it ("comment red for X, blue for Y on THIS story"). Computed
  // unconditionally, above the `enabled` early return below, because hooks
  // must run in the same order on every render — this one previously sat
  // after the early return and broke that rule.
  const groupedByStory = useMemo(() => {
    const groups = new Map();
    for (const r of rules) {
      const key = r.story_media_id || "unknown";
      if (!groups.has(key)) {
        groups.set(key, { story_media_id: key, thumbnail_url: r.thumbnail_url, expires_at: r.expires_at, rules: [] });
      }
      groups.get(key).rules.push(r);
    }
    return [...groups.values()].sort((a, b) => new Date(b.rules[0]?.created_at || 0) - new Date(a.rules[0]?.created_at || 0));
  }, [rules]);

  if (!enabled) {
    return (
      <div className="rounded-[2rem] border border-ink/10 bg-white p-8 md:p-10" data-testid="story-automations-locked">
        <div className="flex items-start gap-4">
          <span className="grid h-14 w-14 flex-none place-items-center rounded-3xl bg-ink/5 text-ink/60">
            <Lock size={22} />
          </span>
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Story DM Automation</p>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tight">
              Unlock story automations on Growth &amp; Pro
            </h3>
            <p className="mt-2 max-w-xl text-sm text-ink/60">
              Turn story replies into private checkouts — reply "red" for the red shade, "blue" for
              the blue one, each keyword sending its own product. Available on Growth (₹2,899) and
              Pro (₹6,499).
            </p>
            <a
              href="/plans"
              data-testid="story-automations-upgrade-cta"
              className="cta-glow mt-5 inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
            >
              Upgrade to Growth <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      product_id: rule.product_id != null ? String(rule.product_id) : "",
      story_media_id: rule.story_media_id || "",
      thumbnail_url: rule.thumbnail_url || "",
      match_type: rule.match_type === "any" ? "any" : "keyword",
      keywords: (rule.keywords || []).join(", "),
    });
    scrollToForm();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) return toast.error("Pick a product for this rule.");
    if (!editingId && !form.story_media_id) return toast.error("Pick a story for this rule.");
    const kws = form.keywords.split(",").map((s) => s.trim()).filter(Boolean);
    if (form.match_type === "keyword" && kws.length === 0) {
      return toast.error("Add at least one keyword, or switch to \"Any reply\".");
    }

    setCreating(true);
    try {
      if (editingId) {
        // story_media_id / thumbnail_url are intentionally NOT sent on edit —
        // the backend doesn't accept changing which story a rule points to;
        // create a new rule for a different story instead.
        await api.patch("/merchant/story-automations", {
          id: editingId,
          product_id: form.product_id,
          match_type: form.match_type,
          keywords: form.match_type === "keyword" ? kws : [],
        });
        toast.success("Story automation updated.");
      } else {
        await api.post("/merchant/story-automations", {
          product_id: form.product_id,
          story_media_id: form.story_media_id,
          thumbnail_url: form.thumbnail_url || null,
          match_type: form.match_type,
          keywords: form.match_type === "keyword" ? kws : [],
          active: true,
        });
        toast.success("Story automation created — live for as long as your story is up.");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editingId ? "Could not update rule." : "Could not create rule."));
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (rule) => {
    if (togglingId) return; // one toggle in flight at a time is plenty
    setTogglingId(rule.id);
    try {
      await api.patch("/merchant/story-automations", { id: rule.id, active: !rule.active });
      await load({ silent: true }); // silent: keep the grouped list visible while refreshing
    } catch {
      toast.error("Could not update rule.");
    } finally {
      setTogglingId(null);
    }
  };

  const remove = (rule) => setDeleteTarget(rule);

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.post("/merchant/story-automations/delete", { id: deleteTarget.id });
      toast.success("Rule deleted.");
      if (editingId === deleteTarget.id) resetForm();
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Could not delete rule.");
    } finally {
      setDeleting(false);
    }
  };

  const selectedProduct = catalog.find((p) => String(p.id) === String(form.product_id));

  return (
    <div className="space-y-6" data-testid="story-automations-panel">
      {/* Header */}
      <div className="overflow-hidden rounded-[2rem] bg-sunset-gradient p-8 text-white shadow-lg md:p-10">
        <div className="flex items-start gap-3">
          <Sparkles size={22} />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/85">
              Story DM Automation · {merchant?.plan_meta?.name} plan
            </p>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tight md:text-3xl">
              Turn story replies into paid orders.
            </h3>
            <p className="mt-2 max-w-xl text-white/85">
              Pick one of your live stories, set a keyword per product — "red" for the red
              shade, "blue" for the blue one — and we'll DM the right product card the moment
              someone replies. It stays live for as long as your story does — 24 hours normally,
              48 hours if you're on IG Plus — then removes itself automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Create / edit rule */}
      <form
        ref={formRef}
        onSubmit={submit}
        className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8"
        data-testid="story-automation-create-form"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {editingId ? <Pencil size={16} className="text-hotpink" /> : <Plus size={16} className="text-hotpink" />}
            <p className="font-display text-lg font-black tracking-tight">
              {editingId ? "Edit story automation" : "Create story automation"}
            </p>
          </div>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              data-testid="story-rule-cancel-edit"
              className="inline-flex items-center gap-1 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-ink/5"
            >
              <X size={12} /> Cancel
            </button>
          )}
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {/* Story picker — only shown when creating, not editing (can't repoint an existing rule) */}
          {!editingId && (
            <div className="text-sm md:col-span-2">
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">
                Which live story?
              </span>

              {/* Pre-selected via "Add another keyword to this story" — shown instead of the live
                  picker, since that story may no longer appear in the freshly-fetched active-stories
                  list (Instagram's own story can expire before our automation row does) and would
                  otherwise look unselected/confusing below. */}
              {form.story_media_id && !igStories.some((s) => s.id === form.story_media_id) ? (
                <div className="flex items-center gap-3 rounded-2xl border border-hotpink/30 bg-hotpink/[0.03] p-3">
                  <span className="h-12 w-12 flex-none overflow-hidden rounded-xl bg-ink/5">
                    {form.thumbnail_url ? (
                      <SafeThumb src={form.thumbnail_url} />
                    ) : (
                      <span className="grid h-full w-full place-items-center text-ink/30">
                        <Film size={16} />
                      </span>
                    )}
                  </span>
                  <div className="flex-1 text-xs">
                    <p className="font-semibold text-ink">Adding another keyword to this story</p>
                    <p className="text-ink/50">Pick a product and keyword below, then Create rule.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, story_media_id: "", thumbnail_url: "" })}
                    className="text-xs font-semibold text-hotpink underline"
                  >
                    Change story
                  </button>
                </div>
              ) : (
                <>
                  {igStoriesLoading && (
                    <p className="flex items-center gap-1.5 text-xs text-ink/50">
                      <Loader2 size={12} className="animate-spin" /> Loading your active stories…
                    </p>
                  )}

                  {!igStoriesLoading && igStoriesError && (
                    <p className="mb-1.5 text-xs text-hotpink">
                      {igStoriesError}{" "}
                      <button type="button" onClick={() => setIgStoriesFetched(false)} className="font-semibold underline">
                        Retry
                      </button>
                    </p>
                  )}

                  {!igStoriesLoading && !igStoriesError && igStories.length === 0 && (
                    <p className="text-xs text-ink/50">
                      No active stories right now — post one on Instagram, then come back here.
                    </p>
                  )}

                  {!igStoriesLoading && igStories.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {igStories.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setForm({ ...form, story_media_id: item.id, thumbnail_url: item.thumbnail || "" })}
                          data-testid={`ig-story-${item.id}`}
                          className={`relative h-16 w-16 flex-none overflow-hidden rounded-xl border-2 ${
                            form.story_media_id === item.id ? "border-hotpink" : "border-transparent"
                          }`}
                        >
                          {item.thumbnail ? (
                            <SafeThumb src={item.thumbnail} />
                          ) : (
                            <span className="grid h-full w-full place-items-center bg-ink/5 text-ink/30">
                              <Film size={16} />
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Product */}
          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">Product</span>
            <select
              value={form.product_id}
              onChange={(e) => setForm({ ...form, product_id: e.target.value })}
              data-testid="story-rule-product"
              disabled={catalogLoading}
              className="w-full rounded-2xl border border-ink/15 bg-white px-3 py-2 focus:border-hotpink focus:outline-none disabled:opacity-60"
            >
              <option value="">
                {catalogLoading ? "Loading catalog…" : "Select a product…"}
              </option>
              {catalog.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} — {formatMoney(p.price, merchant?.currency)}{p.sold_out ? " (Sold out)" : ""}
                </option>
              ))}
            </select>
            {!catalogLoading && catalog.length === 0 && (
              <p className="mt-1.5 text-xs text-ink/50">
                No products yet — add one in your Catalog first.
              </p>
            )}
          </label>

          {/* Match type */}
          <div className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">Match</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, match_type: "keyword" })}
                data-testid="story-rule-match-keyword"
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                  form.match_type === "keyword" ? "bg-ink text-cream" : "border border-ink/15 bg-white"
                }`}
              >
                Specific keyword(s)
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, match_type: "any" })}
                data-testid="story-rule-match-any"
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                  form.match_type === "any" ? "bg-ink text-cream" : "border border-ink/15 bg-white"
                }`}
              >
                Any reply
              </button>
            </div>
            <p className="mt-1.5 text-xs text-ink/40">
              Add one rule per keyword to sell multiple variants off the same story — e.g. "red" →
              Red Lipstick, "blue" → Blue Lipstick.
            </p>
          </div>

          {form.match_type === "keyword" && (
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">
                Keywords <span className="text-ink/40">(comma-separated)</span>
              </span>
              <input
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="red, maroon"
                data-testid="story-rule-keywords"
                className="w-full rounded-2xl border border-ink/15 px-3 py-2 focus:border-hotpink focus:outline-none"
              />
            </label>
          )}
        </div>

        {selectedProduct && (
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-ink/10 bg-ink/[0.02] p-3">
            {selectedProduct.image ? (
              <img src={selectedProduct.image} alt="" className="h-10 w-10 rounded-xl object-cover" />
            ) : null}
            <div className="text-xs text-ink/60">
              <p className="font-semibold text-ink">{selectedProduct.title}</p>
              <p>{formatMoney(selectedProduct.price, merchant?.currency)} · this is the product card repliers will be shown</p>
              {selectedProduct.sold_out && (
                <p className="mt-1 font-semibold text-hotpink">
                  Currently sold out — repliers will still get this card, so pause the rule until it's back in stock.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <button
            type="submit"
            disabled={creating}
            data-testid="story-rule-create-btn"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-70"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : editingId ? <Pencil size={14} /> : <Plus size={14} />}
            {editingId ? "Save changes" : "Create rule"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              disabled={creating}
              className="text-sm font-semibold text-ink/50 hover:text-ink"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Rules list — grouped by story: one card per story, one row per keyword */}
      <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="story-automation-rules-list">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-hotpink" />
          <p className="font-display text-lg font-black tracking-tight">Active story automations</p>
        </div>
        {loading ? (
          <p className="mt-6 text-sm text-ink/50">Loading…</p>
        ) : groupedByStory.length === 0 ? (
          <p className="mt-6 text-sm text-ink/50">No story automations yet. Create your first above.</p>
        ) : (
          <div className="mt-6 space-y-4">
            {groupedByStory.map((group) => (
              <div key={group.story_media_id} className="rounded-2xl border border-ink/10 p-4" data-testid={`story-group-${group.story_media_id}`}>
                <div className="flex items-center gap-3">
                  <span className="relative h-12 w-12 flex-none overflow-hidden rounded-xl border border-ink/10 bg-ink/5">
                    {group.thumbnail_url ? (
                      <SafeThumb src={group.thumbnail_url} />
                    ) : (
                      <span className="grid h-full w-full place-items-center text-ink/30">
                        <Film size={16} />
                      </span>
                    )}
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.12em] text-ink/50">Story</p>
                    <p className="text-xs text-ink/40">
                      {isRuleExpired(group.expires_at)
                        ? "Expired — will be removed shortly"
                        : "Live for as long as your story is up"}
                    </p>
                  </div>
                </div>

                <ul className="mt-3 space-y-2">
                  {group.rules.map((r) => {
                    const isToggling = togglingId === r.id;
                    return (
                      <li
                        key={r.id}
                        className={`flex flex-col gap-3 rounded-xl border p-3 md:flex-row md:items-center md:justify-between ${
                          editingId === r.id ? "border-hotpink/40 bg-hotpink/[0.03]" : "border-ink/10"
                        }`}
                        data-testid={`story-rule-row-${r.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-display text-base font-black">
                              {r.product_name || `Product #${r.product_id}`}
                            </p>
                            {r.active ? (
                              <span className="rounded-full bg-mint px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-ink">Active</span>
                            ) : (
                              <span className="rounded-full bg-ink/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-ink/60">Paused</span>
                            )}
                            {r.product_sold_out && (
                              <span className="rounded-full bg-hotpink/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-hotpink">
                                Sold out
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-ink/60">
                            {r.match_type === "any" ? (
                              "Triggers on any reply"
                            ) : (
                              <>
                                Triggers on:{" "}
                                {(r.keywords || []).map((k) => (
                                  <code key={k} className="mx-0.5 rounded bg-ink/5 px-1.5 py-0.5">{k}</code>
                                ))}
                              </>
                            )}
                          </p>
                          <p className="mt-1.5 text-xs text-ink/50">
                            {r.dm_sent} sent
                            {r.dm_failed > 0 && ` · ${r.dm_failed} failed`}
                            {r.dm_last_sent_at && ` · last ${new Date(r.dm_last_sent_at).toLocaleDateString()}`}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEdit(r)}
                            disabled={isToggling}
                            data-testid={`story-rule-edit-${r.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <Pencil size={12} /> Edit
                          </button>
                          <button
                            onClick={() => toggle(r)}
                            disabled={isToggling}
                            data-testid={`story-rule-toggle-${r.id}`}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                              isToggling
                                ? "cursor-not-allowed border-ink/10 bg-ink/5 text-ink/40"
                                : "border-ink/15 bg-white hover:bg-ink/5"
                            }`}
                          >
                            {isToggling ? <Loader2 size={12} className="animate-spin" /> : <Power size={12} />}
                            {isToggling ? (r.active ? "Pausing…" : "Activating…") : r.active ? "Pause" : "Activate"}
                          </button>
                          <button
                            onClick={() => remove(r)}
                            disabled={isToggling}
                            data-testid={`story-rule-delete-${r.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-hotpink/30 bg-white px-3 py-1.5 text-xs font-semibold text-hotpink hover:bg-hotpink/5 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <button
                  type="button"
                  onClick={() => {
                    setForm({ ...EMPTY_FORM, story_media_id: group.story_media_id, thumbnail_url: group.thumbnail_url || "" });
                    scrollToForm();
                    toast.info("Scoped to this story — pick a product and keyword below, then Create rule.");
                  }}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-hotpink hover:underline"
                  data-testid={`story-add-keyword-${group.story_media_id}`}
                >
                  <Plus size={12} /> Add another keyword to this story
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center">
            <p className="font-display text-xl font-black tracking-tight">Delete this automation rule?</p>
            <p className="mt-2 text-sm text-ink/60">
              This stops the rule from triggering on new story replies. This can't be undone.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 rounded-full bg-hotpink py-2.5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
